// src/hooks/mediaConversion/useStableJobPercent.ts
import { useEffect, useRef, useState } from "react";

type Meta = {
  overallStatus?: "queued" | "processing" | "done" | "failed" | "canceled" | "idle";
  stage?: string | null;
  stageProgress?: number | null;           // 0..1
  stageCompletedWeight?: number | null;    // >=0
};

/**
 * Stable percent for UI, BUT rejects impossible outliers from backend.
 * - Resets when jobKey changes
 * - Monotonic for normal updates
 * - Ignores "stageProgress === 1" spikes when stageCompletedWeight did NOT advance
 */
export function useStableJobPercent(
  backendPercent: number | null | undefined,
  jobKey: string | null,
  meta?: Meta
): number {
  const [displayPercent, setDisplayPercent] = useState(0);

  const lastPercentRef = useRef(0);
  const lastJobKeyRef = useRef<string | null>(null);

  const lastStageRef = useRef<string | null>(null);
  const lastStageProgressRef = useRef<number | null>(null);
  const lastCompletedWeightRef = useRef<number | null>(null);

  // Reset when job identity changes
  useEffect(() => {
    if (jobKey !== lastJobKeyRef.current) {
      lastJobKeyRef.current = jobKey;

      lastPercentRef.current = 0;
      setDisplayPercent(0);

      lastStageRef.current = null;
      lastStageProgressRef.current = null;
      lastCompletedWeightRef.current = null;
    }
  }, [jobKey]);

  useEffect(() => {
    if (backendPercent == null) return;

    const clampedPct = Math.max(0, Math.min(100, backendPercent));

    const stage = meta?.stage ?? null;
    const sp = meta?.stageProgress ?? null;
    const cw = meta?.stageCompletedWeight ?? null;
    const overall = meta?.overallStatus;

    // --- Outlier guard (based on your logs) ---
    // If backend suddenly reports stageProgress == 1 for the SAME stage,
    // but completed weight didn't move, it's likely a glitch spike.
    const prevStage = lastStageRef.current;
    const prevSp = lastStageProgressRef.current;
    const prevCw = lastCompletedWeightRef.current;

    const sameStage = stage != null && prevStage != null && stage === prevStage;
    const completedWeightUnchanged =
      cw != null && prevCw != null ? cw === prevCw : true;

    const suspiciousFullSpike =
      overall === "processing" &&
      sameStage &&
      completedWeightUnchanged &&
      sp != null &&
      sp >= 0.999 &&
      prevSp != null &&
      prevSp < 0.95; // "jumped to 1" from a low value (exactly your case)

    // Update refs for next run
    lastStageRef.current = stage;
    lastStageProgressRef.current = sp;
    lastCompletedWeightRef.current = cw;

    if (suspiciousFullSpike) {
      // Ignore this update entirely (do not lock percent at 50% etc.)
      return;
    }

    // Normal monotonic behavior (no drift)
    const stable = Math.max(lastPercentRef.current, clampedPct);
    if (stable !== lastPercentRef.current) {
      lastPercentRef.current = stable;
      setDisplayPercent(stable);
    }
  }, [backendPercent, meta?.stage, meta?.stageProgress, meta?.stageCompletedWeight, meta?.overallStatus]);

  return displayPercent;
}
